import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import { assets } from "../data/siteData";

/* ---------------- ANIMATIONS ---------------- */

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const stagger = {
  show: {
    transition: { staggerChildren: 0.2 },
  },
};

const slideLeft = {
  hidden: { opacity: 0, x: -60 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7 },
  },
};

/* ---------------- DATA ---------------- */

const recognitionLogos = [
  {logo: assets.gwp, link: "https://www.gwp.org/en/GWP-Mediterranean/WE-ACT/News-List-Page/20232/30-finalists-water-changemaker-awards-2023/"},
 { logo: assets.ciscoa, link: "https://blogs.cisco.com/csr/meet-hydrotec-solutions-an-india-impact-runner-up-of-the-cisco-global-problem-solver-challenge-2020dtidpblgblg001473oidpstoth022646" },
 { logo: assets.gist, link: "https://venturewell.org/blog/world-water-day/" },
 { logo: assets.bayer, link: "https://getinthering.co/discover-bayer-social-innovation-award-winners/" },
 { logo: assets.bricsa, link: "https://bricswomen.com/2025/07/06/brics-women-led-startup-contest-and-solutions-awards-announce-2025-winners/" },
 { logo: assets.wtia, link: "https://www.niti.gov.in/sites/default/files/2020-03/Coffee_Table_Book.pdf" },
 { logo: assets.una, link: "https://2021-2025.state.gov/un-ocean-conference-2022/" },
 { logo: assets.singaporea, link: "https://www.linkedin.com/posts/siww_%F0%9D%90%80%F0%9D%90%A5%F0%9D%90%A5-%F0%9D%90%A0%F0%9D%90%9E%F0%9D%90%9A%F0%9D%90%AB%F0%9D%90%9E%F0%9D%90%9D-%F0%9D%90%AE%F0%9D%90%A9-%F0%9D%90%9A%F0%9D%90%A7%F0%9D%90%9D-%F0%9D%90%A5%F0%9D%90%A8%F0%9D%90%A8-activity-7208499893395476480-3NP5/" },
 { logo: assets.slt, link: "https://yourstory.com/herstory/2020/04/startup-iot-enabled-drinking-water-atm-hydrotec" },
 { logo: assets.leadersa, link: "https://www.facebook.com/watch/?v=1218474175296289" },
 { logo: assets.ftifc, link: "https://www.facebook.com/IFCwbg/photos/access-to-safe-drinking-water-is-often-a-challenge-in-rural-india-2020-ftifcawar/10158202294930783/" },
 {logo: assets.qualcom, link: "https://yourstory.com/2021/08/qualcomm-women-entrepreneur-india-finalists"},

  
];

const featuredPress = {
  source: "YourStory",
  title:
    "Startup built IoT-enabled Water ATMs dispensing water at ₹1/L",
  excerpt:
    "Arosia Water delivers affordable, monitored drinking water through smart kiosks using IoT, automation, and scalable infrastructure.",
  link:
    "https://yourstory.com/herstory/2020/04/startup-iot-enabled-drinking-water-atm-hydrotec",
  image: assets.news,
};

const pressArticles = [
  {
    source: "YourStory",
    title: "IoT-enabled water ATM startup story",
    image: assets.press1,
    link:
      "https://yourstory.com/herstory/2020/04/startup-iot-enabled-drinking-water-atm-hydrotec",
  },
  {
    source: "The Water Network",
    title: "The story of Arosia Water ATM",
    image: assets.press2,
    link:
      "https://thewaternetwork.com/article-FfV/the-story-of-arosia-water-atm-3HjwIuEDyAxL_UD0zJGRgw",
  },
];

/* ---------------- COMPONENT ---------------- */

export default function PressPage() {
  return (
    <div className="bg-white text-[#111]">
      <SEO
        title="News & Press | Hydrotec Solutions | Water Technology Kolkata, West Bengal"
        description="Latest news, press coverage, and updates from Hydrotec Solutions — an award-winning water technology company serving Kolkata, Newtown, Rajarhat, and West Bengal with water treatment and smart water systems."
        keywords="Hydrotec news Kolkata, water technology news West Bengal, water company news Kolkata, Hydrotec press coverage"
        canonical="https://hydroteclimited.com/news"
      />

      {/* HERO */}
      <motion.section
        initial="hidden"
        animate="show"
        variants={fadeUp}
        className="relative h-screen flex items-center justify-center text-center bg-black text-white overflow-hidden"
      >
        <motion.img
          src={assets.news}
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2 }}
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />

        <div className="relative z-10 max-w-4xl px-6">
          <h1 className="text-5xl md:text-7xl font-semibold">
            Recognized globally.
            <br />
            Built for impact.
          </h1>

          <p className="mt-6 text-lg text-white/70">
            From grassroots deployments to global platforms,
            Arosia Water is redefining access to safe drinking water.
          </p>
        </div>
      </motion.section>

      {/* LOGOS */}
            <section className="py-24 text-center">
              <p className="text-xs uppercase tracking-[0.8em] text-gray-900">
                Recognized by
              </p>
      
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                className="mt-12 flex justify-center gap-16 flex-wrap"
              >
                {recognitionLogos.map((item, i) => (
                  <a
                    key={i}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <motion.img
                      src={item.logo}
                      variants={fadeUp}
                      className="h-[100px] opacity-60 hover:opacity-100 transition duration-300 cursor-pointer"
                    />
                  </a>
                ))}
              </motion.div>
            </section>
      
      {/* FEATURED */}
      <motion.section
        variants={stagger}
        initial="hidden"
        whileInView="show"
        className="grid lg:grid-cols-2 min-h-[90vh]"
      >
        <motion.img
          src={featuredPress.image}
          variants={fadeUp}
          className="w-full h-full object-cover"
        />

        <motion.div
          variants={fadeUp}
          className="flex items-center px-8 lg:px-20"
        >
          <div>
            <p className="text-sm uppercase text-gray-400">
              {featuredPress.source}
            </p>

            <h2 className="text-4xl font-semibold mt-4">
              {featuredPress.title}
            </h2>

            <p className="mt-6 text-gray-600">
              {featuredPress.excerpt}
            </p>

            <a
              href={featuredPress.link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-block font-semibold"
            >
              Read story →
            </a>
          </div>
        </motion.div>
      </motion.section>

      {/* PRESS GRID WITH IMAGES */}
      <section className="py-32 bg-[#f5f5f7]">
        <h2 className="text-center text-5xl font-semibold">
          In the press
        </h2>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto px-6"
        >
          {pressArticles.map((item, i) => (
            <motion.a
              key={i}
              variants={fadeUp}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={item.image}
                  className="w-full h-[260px] object-cover transition duration-700 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />

                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <p className="text-xs opacity-80">{item.source}</p>
                  <h3 className="text-lg font-semibold">
                    {item.title}
                  </h3>
                </div>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </section>

      {/* CTA */}
      <section className="py-32 text-center">
        <h2 className="text-4xl font-semibold">
          Press & media enquiries
        </h2>

        <Link
          to="/contact"
          className="mt-8 inline-block text-lg font-semibold text-blue-600"
        >
          Contact us →
        </Link>
      </section>

    </div>
  );
}